import { Modal } from "bootstrap";

/**
 * Exit-intent basket capture — checkout only. STDM-236.
 *
 * Hands the captured address to AutomateWoo by dispatching a bubbling `change` on an input
 * carrying its own .automatewoo-capture-guest-email selector. AutomateWoo binds
 * 'blur change' delegated on document, so a native bubbling event reaches its jQuery
 * handler and its presubmit AJAX does the rest. No custom endpoint, nothing to keep in sync.
 *
 * Fires once per session. Desktop uses a genuine mouseleave off the top of the viewport;
 * touch devices have no mouseleave, so they get a fast-upward-scroll proxy instead.
 */

const STORAGE_KEY = "studmarcExitIntentShown";
const SCROLL_VELOCITY_THRESHOLD = 12; // px per event, upward
const SCROLL_ARM_OFFSET = 120; // only after the user has scrolled down this far

const exitIntent = {
    el: null,
    modal: null,
    form: null,
    emailField: null,
    consentField: null,
    errorEl: null,
    successEl: null,
    shown: false,
    lastScrollY: 0,

    init: function () {
        this.el = document.querySelector("[data-exit-intent]");
        if (!this.el) {
            return; // not checkout, or the modal was not rendered
        }

        this.form = this.el.querySelector("[data-exit-intent-form]");
        this.emailField = this.el.querySelector(".automatewoo-capture-guest-email");
        this.consentField = this.el.querySelector("#exit-intent-consent");
        this.errorEl = this.el.querySelector(".exit-intent__error");
        this.successEl = this.el.querySelector("[data-exit-intent-success]");

        if (!this.form || !this.emailField || !this.consentField) {
            return;
        }

        if (this.alreadyShown()) {
            return;
        }

        this.modal = new Modal(this.el);
        this.bindTriggers();
        this.bindForm();
    },

    /**
     * sessionStorage can throw (private mode, blocked site data) — a failure to read must
     * not stop the page working, so treat an unreadable store as "not yet shown".
     */
    alreadyShown: function () {
        try {
            return window.sessionStorage.getItem(STORAGE_KEY) === "1";
        } catch (e) {
            return false;
        }
    },

    markShown: function () {
        this.shown = true;
        try {
            window.sessionStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {
            // Per-session capping is a nicety; losing it is not worth a broken checkout.
        }
    },

    bindTriggers: function () {
        const instance = this;

        // Desktop: pointer leaves through the top of the viewport.
        document.addEventListener("mouseleave", function (e) {
            if (e.clientY <= 0) {
                instance.open();
            }
        });

        // Touch: no mouseleave exists, so approximate intent with a fast flick upwards
        // once the user is far enough down the page for it to mean something.
        this.lastScrollY = window.scrollY;
        window.addEventListener(
            "scroll",
            function () {
                const current = window.scrollY;
                const delta = instance.lastScrollY - current;
                instance.lastScrollY = current;

                if (current > SCROLL_ARM_OFFSET && delta > SCROLL_VELOCITY_THRESHOLD) {
                    instance.open();
                }
            },
            { passive: true }
        );
    },

    open: function () {
        if (this.shown || this.alreadyShown()) {
            return;
        }

        this.markShown();
        this.modal.show();

        // Bootstrap moves focus to the dialog; put it on the field the user needs.
        const instance = this;
        this.el.addEventListener(
            "shown.bs.modal",
            function () {
                instance.emailField.focus();
            },
            { once: true }
        );
    },

    bindForm: function () {
        const instance = this;

        this.form.addEventListener("submit", function (e) {
            e.preventDefault();
            instance.submit();
        });

        // Clear a standing error as soon as the user starts fixing it.
        this.emailField.addEventListener("input", function () {
            instance.clearError();
        });
        this.consentField.addEventListener("change", function () {
            instance.clearError();
        });
    },

    submit: function () {
        const email = this.emailField.value.trim();

        if (!email || !this.emailField.checkValidity()) {
            this.showError("Please enter a valid email address.");
            this.emailField.focus();
            return;
        }

        // Consent gates the handoff: if the box is not ticked, AutomateWoo never receives
        // the address, so capture and consent are the same event.
        if (!this.consentField.checked) {
            this.showError("Please tick the box so we can email you the link.");
            this.consentField.focus();
            return;
        }

        this.clearError();
        this.handOffToAutomateWoo(email);
        this.showSuccess();
    },

    /**
     * AutomateWoo's presubmit script listens for 'blur change' on its capture selectors,
     * delegated on document. The field already carries one of those selectors, so a
     * bubbling native change event is picked up by its handler.
     */
    handOffToAutomateWoo: function (email) {
        this.emailField.value = email;
        this.emailField.dispatchEvent(new Event("change", { bubbles: true }));
    },

    showError: function (message) {
        if (!this.errorEl) {
            return;
        }
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
    },

    clearError: function () {
        if (!this.errorEl) {
            return;
        }
        this.errorEl.textContent = "";
        this.errorEl.hidden = true;
    },

    showSuccess: function () {
        this.form.hidden = true;
        if (this.successEl) {
            this.successEl.hidden = false;
        }

        // Give the capture request a moment to leave before the dialog closes.
        const instance = this;
        window.setTimeout(function () {
            instance.modal.hide();
        }, 2500);
    },
};

export default exitIntent;
